import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthRole } from '../../../services/auth/role.service';
import { ApiDigitalAuthPolicy } from '../../../services/auth/policy.service';
import { Role } from '../../../models/role.model';
import { Policy } from '../../../models/policy.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppPreloader from '../../../components/Preloader';

export interface RoleViewPageProps {};

const RoleViewPage: React.FunctionComponent<RoleViewPageProps> = props => {
  const {setRoute, appServiceForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()
  const {id} = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [role, setRole] = useState<Role | null>(null)
  const [allPolicies, setAllPolicies] = useState<Policy[] | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const loadRole = async (api_key: string) => {
    if (!id) return
    await ApiDigitalAuthRole.getRole(api_key, id).then((response) => {
      if (response.status === 200 && response.data) {
        const r = response.data
        setRole(r)
        setEditName(r.name ?? '')
        setEditDescription(r.description ?? '')
      } else {
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadPolicies = async (api_key: string) => {
    const response = await ApiDigitalAuthPolicy.getPolicies(api_key, false)
    if (response.status === 200) {
      setAllPolicies(Array.isArray(response.data) ? response.data : [])
    } else {
      setAllPolicies([])
    }
  }

  const handleUpdate = async () => {
    if (!appServiceForUser || !id || !editName.trim()) return
    setSaving(true)

    await ApiDigitalAuthRole.updateRole(appServiceForUser.api_key, id, {
      name: editName.trim(),
      description: editDescription.trim()
    }).then((response) => {
      if (response.status === 200) {
        loadRole(appServiceForUser.api_key)
      } else {
        GlobalSwal.fire({
          title: response.data?.message ?? lang.labels.error,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setSaving(false)
    })
  }

  const handleDelete = async () => {
    if (!appServiceForUser || !id) return

    GlobalSwal.fire({
      title: lang.labels.confirmDeleteRole,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAuthRole.deleteRole(appServiceForUser!.api_key, id!).then((response) => {
          if (response.status === 200) {
            navigate('/main/admin/roles')
          } else {
            GlobalSwal.fire({
              title: response.data?.message ?? lang.labels.error,
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleAttachPolicy = async (policy_id: string) => {
    if (!appServiceForUser || !id) return
    await ApiDigitalAuthRole.attachPolicy(appServiceForUser.api_key, id, policy_id).then((response) => {
      if (response.status === 200 || response.status === 201) {
        loadRole(appServiceForUser!.api_key)
      } else {
        GlobalSwal.fire({
          title: response.data?.message ?? lang.labels.error,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleDetachPolicy = async (policy_id: string) => {
    if (!appServiceForUser || !id) return
    await ApiDigitalAuthRole.detachPolicy(appServiceForUser.api_key, id, policy_id).then((response) => {
      if (response.status === 200) {
        loadRole(appServiceForUser!.api_key)
      } else {
        GlobalSwal.fire({
          title: response.data?.message ?? lang.labels.error,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const roleHasPolicy = (policy_id: string): boolean => {
    if (!role || !role.policies) return false
    return role.policies.some(p => (p.id ?? (p as any)._id) === policy_id)
  }

  useEffect(() => {
    if (appServiceForUser) {
      loadRole(appServiceForUser.api_key)
      loadPolicies(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.role}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { role && allPolicies
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bolder">
                  {role.name}
                  {role.is_system && <span className="badge badge-light-info fs-8 ms-2">{lang.labels.system}</span>}
                </h3>
              </div>
              { !role.is_system &&
                <div className="card-toolbar">
                  <button className="btn btn-sm btn-light-danger" type="button" onClick={handleDelete}>{lang.labels.delete}</button>
                </div>
              }
            </div>
            <div className="card-body pt-0">
              { !role.is_system
                ?
                <>
                  <div className="mb-5">
                    <label className="form-label fw-bolder">{lang.labels.name} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="form-label fw-bolder">{lang.labels.description}</label>
                    <textarea
                      className="form-control"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      rows={3}
                      maxLength={500}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <button className="btn btn-primary" type="button" onClick={handleUpdate} disabled={saving || !editName.trim()}>
                      {saving ? lang.labels.loading : lang.labels.save}
                    </button>
                  </div>
                </>
                :
                <div className="text-muted">{lang.labels.systemReadOnly}</div>
              }
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <h4 className="fw-bolder">{lang.labels.attachedPolicies}</h4>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px">{lang.labels.name}</th>
                      <th className="min-w-100px">{lang.labels.type}</th>
                      <th className="min-w-100px">{lang.labels.statements}</th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary fw-bold">
                    { allPolicies.map((policy, index) => {
                      const policyId = policy.id ?? (policy as any)._id
                      const attached = roleHasPolicy(policyId)
                      return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <span className="fw-bolder">{policy.name}</span>
                            {policy.description && <div className="text-muted fs-8">{policy.description}</div>}
                          </td>
                          <td>
                            { policy.is_system
                              ? <span className="badge badge-light-info fs-8">{lang.labels.system}</span>
                              : <span className="badge badge-light fs-8">Custom</span>
                            }
                          </td>
                          <td>
                            <span className="badge badge-light fs-8">{(policy.statements ?? []).length}</span>
                          </td>
                          <td className="text-end">
                            { !role.is_system && (
                              attached
                                ? <button className="btn btn-sm btn-light-danger" type="button" onClick={() => handleDetachPolicy(policyId)}>{lang.labels.detachPolicy}</button>
                                : <button className="btn btn-sm btn-light-success" type="button" onClick={() => handleAttachPolicy(policyId)}>{lang.labels.attachPolicy}</button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default RoleViewPage;
